/**
 * Persisted market-language filter preference (per wallet + guest fallback).
 */
import { MARKET_LANGUAGE_OPTIONS } from '../config/marketLanguages';

const LANG_PREFS_KEY = 'abc-lang-prefs-v1';
const LEGACY_SORT_KEY = 'abc-sort-prefs';

export type LangPrefsStore = {
  guest: string | null;
  wallets: Record<string, string | null>;
};

function normalizeLang(code: string | null | undefined): string | null {
  if (code == null || code === '') return null;
  const v = String(code).trim().toLowerCase();
  return v || null;
}

function readStore(): LangPrefsStore {
  try {
    const raw = localStorage.getItem(LANG_PREFS_KEY);
    if (raw) {
      const p = JSON.parse(raw) as Partial<LangPrefsStore>;
      const wallets: Record<string, string | null> = {};
      if (p.wallets && typeof p.wallets === 'object') {
        for (const [k, v] of Object.entries(p.wallets)) {
          wallets[k.toLowerCase()] = normalizeLang(v as string | null);
        }
      }
      return { guest: normalizeLang(p.guest), wallets };
    }
  } catch {
    // ignore
  }

  // Migrate legacy Home-only pref from abc-sort-prefs.
  try {
    const legacy = localStorage.getItem(LEGACY_SORT_KEY);
    if (legacy) {
      const p = JSON.parse(legacy) as Record<string, unknown>;
      const migrated = normalizeLang(p?.liveLangFilter as string | null);
      if (migrated) {
        const store: LangPrefsStore = { guest: migrated, wallets: {} };
        writeStore(store);
        return store;
      }
    }
  } catch {
    // ignore
  }

  return { guest: null, wallets: {} };
}

function writeStore(store: LangPrefsStore) {
  try {
    localStorage.setItem(LANG_PREFS_KEY, JSON.stringify(store));
  } catch {
    // ignore
  }
}

export function loadLangPrefForScope(scopeKey: string | null | undefined): string | null {
  const store = readStore();
  if (scopeKey) {
    const wallet = scopeKey.toLowerCase();
    if (Object.prototype.hasOwnProperty.call(store.wallets, wallet)) {
      return store.wallets[wallet] ?? null;
    }
  }
  return store.guest;
}

export function saveLangPrefForScope(scopeKey: string | null | undefined, lang: string | null) {
  const store = readStore();
  const normalized = normalizeLang(lang);
  if (scopeKey) {
    store.wallets[scopeKey.toLowerCase()] = normalized;
  } else {
    store.guest = normalized;
  }
  writeStore(store);
}

/** Full ISO list for selectors (includes selected lang even if not in catalog). */
export function languageSelectorOptions(selected: string | null): string[] {
  const set = new Set<string>(MARKET_LANGUAGE_OPTIONS);
  if (selected) set.add(selected.trim().toLowerCase());
  return [...set].sort((a, b) => a.localeCompare(b));
}

export function marketMatchesLangFilter(
  marketAddress: string,
  langByMarket: Record<string, string>,
  langFilter: string | null,
): boolean {
  if (!langFilter) return true;
  const want = String(langFilter).trim().toLowerCase();
  const lang = langByMarket[(marketAddress || '').toLowerCase()];
  const normalized = String(lang ?? '').trim().toLowerCase();
  // Fail open until tweet_data lang is loaded — avoids hiding live markets on Home
  // while Explore (often guest / All languages) still shows them.
  if (!normalized) return true;
  return normalized === want;
}

export function filterRowsByLang<T extends { market_address?: string | null }>(
  rows: T[],
  langFilter: string | null,
  langByMarket: Record<string, string>,
): T[] {
  if (!langFilter) return rows;
  return rows.filter((r) =>
    marketMatchesLangFilter(r.market_address || '', langByMarket, langFilter),
  );
}
